import React, { useState, useEffect } from "react";
import { IDatabaseLineItem } from "../../LineItemDBCreationToolPage";
import { EstimateType, getRequiredScopesForEstimateType } from "../utils/EstimateType";
import { LineItemOrderObject } from "../../../types";
import { v4 as uuidv4 } from 'uuid';

interface InsertLineItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    estimateType: EstimateType;
    lineItems: IDatabaseLineItem[];
    lineItemOrders: LineItemOrderObject[];
    maxIndex: number;
    onInsert: (lineItemOrderObject: LineItemOrderObject) => void;
}

const InsertLineItemModal: React.FC<InsertLineItemModalProps> = ({
    isOpen,
    onClose,
    estimateType,
    lineItems,
    lineItemOrders,
    maxIndex,
    onInsert,
}) => {
    const [selectedLineItemId, setSelectedLineItemId] = useState<string>("");
    const [insertIndex, setInsertIndex] = useState<number>(maxIndex);

    // Create a set of line item IDs that are already in the order list
    const existingLineItemIds = new Set(lineItemOrders.map(order => order.lineItemId));

    // Filter line items by scope matching the estimate type and not already in the list
    const filteredLineItems = lineItems.filter(item => {
        // Exclude items already in the order list
        if (existingLineItemIds.has(item.id)) return false;

        // Exclude deleted items
        if (item.deleted) return false;

        // Check scope matching
        if (!item.scopes || item.scopes.length === 0) return true;

        const relevantScopes = getRequiredScopesForEstimateType(estimateType).map(scope => scope as string);
        return item.scopes.some(scope => relevantScopes.includes(scope));
    });

    useEffect(() => {
        if (isOpen) {
            setSelectedLineItemId("");
            setInsertIndex(maxIndex);
        }
    }, [isOpen, maxIndex]);

    const handleInsert = () => {
        if (!selectedLineItemId) {
            alert("Please select a line item");
            return;
        }

        onInsert({
            uniqueId: uuidv4(),
            lineItemId: selectedLineItemId,
            index: insertIndex,
        });
        onClose();
    };

    if (!isOpen) return null;

    const selectedLineItem = lineItems.find(item => item.id === selectedLineItemId);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Insert Line Item</h3>

                <div className="space-y-4 mb-6">
                    {/* Line Item Selector */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Line Item
                        </label>
                        <select
                            value={selectedLineItemId}
                            onChange={(e) => setSelectedLineItemId(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">-- Select a line item --</option>
                            {filteredLineItems.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.originalLineItem}
                                </option>
                            ))}
                        </select>
                        {selectedLineItem && (
                            <div className="mt-2 text-sm text-gray-600">
                                <span className="font-medium">Category:</span> {selectedLineItem.category || "N/A"}
                                {selectedLineItem.scopes && selectedLineItem.scopes.length > 0 && (
                                    <>
                                        {" | "}
                                        <span className="font-medium">Scopes:</span> {selectedLineItem.scopes.join(", ")}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Index Selector */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Insert at Index
                        </label>
                        <input
                            type="number"
                            min="0"
                            max={maxIndex}
                            value={insertIndex}
                            onChange={(e) => setInsertIndex(parseInt(e.target.value) || 0)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                            Enter an index between 0 and {maxIndex}
                        </p>
                    </div>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleInsert}
                        disabled={!selectedLineItemId}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        Insert
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InsertLineItemModal;
